import React, { useMemo, useRef } from 'react';
import { MultiOptionsFilterComponent } from 'features/insights/components/compare-bar/report-actions-bar/advance-filters-modal/multi-entities-filter/types';
import {
    Dropdown,
    MultiSelect,
    MultiSelectDropdownMenu,
    MultiSelectItemProps,
    UnifiedSelectBox,
} from '@placer-ui/components';
import { APP_CONSTS } from 'core/constants/app-consts';
import { FilterOperator } from '@placer-ui/types';
import { type AudienceType, EMPLOYEES, RESIDENTS, VISITORS } from 'shared/constants/audience-type';
import { getFeatureFlags } from 'core/flow-control';
import { useGetAudienceTypeLabels } from 'features/insights/hooks/use-get-audience-type-labels';

const AUDIENCE_TYPE_OPTIONS: AudienceType[] = [VISITORS, EMPLOYEES, RESIDENTS];

export const AudienceTypeFilter: MultiOptionsFilterComponent<string[]> = ({
    filter,
    onChange,
    ariaLabel,
}) => {
    const { accessibility_advance_filters_modal_ff } = getFeatureFlags();
    const getAudienceTypeLabel = useGetAudienceTypeLabels();
    const triggerRef = useRef<HTMLDivElement>(null);

    const options: MultiSelectItemProps[] = useMemo(
        () =>
            AUDIENCE_TYPE_OPTIONS.map((option) => ({
                label: getAudienceTypeLabel(option),
                value: option,
                isSelected: filter?.value.some((value) => value === option),
                multi: 'checkbox',
            })) ?? [],
        [filter?.value, getAudienceTypeLabel],
    );

    if (accessibility_advance_filters_modal_ff) {
        return (
            <Dropdown
                appendToParent
                menuWidth={220}
                trigger={
                    <UnifiedSelectBox
                        ref={triggerRef}
                        aria-label={ariaLabel}
                        testId="audience-type-filter"
                        value={options
                            .filter(({ isSelected }) => isSelected)
                            .map(({ label }) => label)
                            .join(', ')}
                    />
                }
            >
                <MultiSelectDropdownMenu
                    items={options}
                    onSelection={(options) => {
                        const selectedValues = options
                            .filter((option) => option.isSelected)
                            .map((option) => option.value);
                        onChange({
                            operator: APP_CONSTS.filter.operators.IN as FilterOperator,
                            value: selectedValues,
                        });
                        triggerRef.current?.focus();
                    }}
                />
            </Dropdown>
        );
    }

    return (
        <MultiSelect
            autoFocus={false}
            align="start"
            side="bottom"
            items={options}
            data-testid="audience-type-filter"
            onSelection={(options) => {
                const selectedValues = options
                    .filter((option) => option.isSelected)
                    .map((option) => option.value);
                onChange({
                    operator: APP_CONSTS.filter.operators.IN as FilterOperator,
                    value: selectedValues,
                });
            }}
            appendToParent
        />
    );
};
